import React from 'react';
import type { PrintColumn, PrintDocumentModel } from '../types';
import { buildSimplePrintDocument } from './simple-document';
import { LINE_HEIGHT_PX } from '../page-template';
import { getCompanyLetterhead } from '../company-letterhead';
import type { PurchaseOrderDTO, PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import type { CompanyPrintInfo } from '@/components/sales-invoice/PrintableSalesInvoice';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import {
  computePoPrintCommercial,
  computePoPrintGrandTotal,
  computePoPrintVatSummary,
  formatPoAmount,
  formatPoAmountInWords,
  formatPoReferenceRows,
  formatPrintDate,
  formatSupplierPrintAddress,
  formatSupplierPrintTrn,
  formatTrnDisplay,
  lineHasDeliverySchedule,
  lineHasDiscountColumns,
  lineHasSpecColumn,
  lineHasVatColumns,
  lineTaxTypeLabel,
} from '@/utils/purchaseOrderPrint';
import { projectLabel } from '@/utils/projectDisplay';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { PO_PAYMENT_TERM_MILESTONE, PO_MILESTONE_FULFILLMENT_LABELS } from '@/constants/poMilestoneTypes';
import { formatQuantity, formatPercent } from '@/utils/format';

function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Row-based estimate for PO totals table (subtotal, VAT, optional rows, grand total). */
function estimatePoTotalsBlockHeight(po: PurchaseOrderDTO, discount = num(po.discount_amount)): number {
  let rows = 2;
  if (discount > 0) rows += 2;
  if (num(po.freight_charges) > 0) rows += 1;
  if (num(po.other_charges) > 0) rows += 1;
  if (num(po.total_overhead) > 0) rows += 1;
  rows += 1;
  return 24 + rows * 17;
}

export function buildPurchaseOrderPrintDocument(
  po: PurchaseOrderDTO,
  items: PurchaseOrderItemDTO[],
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  approvalSigners: ApprovalPrintSigner[] = [],
  options?: { spotPurchaserName?: string | null }
): PrintDocumentModel {
  const currency = po.currency || 'AED';
  const sorted = [...items].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));

  const showDiscCols = lineHasDiscountColumns(sorted);
  const showSpecCol = lineHasSpecColumn(sorted);
  const showDelCol = lineHasDeliverySchedule(sorted);
  const showVatCols = lineHasVatColumns(sorted, po.vat_applicable);
  const showProjectCol = sorted.some((line) => projectLabel(line) !== '—');

  const poDate = formatPrintDate(po.po_date);
  const deliveryDate = formatPrintDate(po.delivery_date);
  const refRows = formatPoReferenceRows(po, items, options);
  const trn = formatTrnDisplay(companyInfo.taxNumber);
  const supplierAddress = formatSupplierPrintAddress(po);
  const supplierTrn = formatSupplierPrintTrn(po);
  const revNo = po.revision_number ?? 1;

  const commercial = computePoPrintCommercial(po, sorted);
  const subtotal = commercial.gross;
  const totalAfterDiscount = commercial.after;
  const vatSummary = computePoPrintVatSummary(po, sorted);
  const grand = computePoPrintGrandTotal(po, sorted, vatSummary);

  const deliveryDisplay =
    (po.delivery_terms || '').trim() || (deliveryDate ? `By ${deliveryDate}` : '');
  const paymentDisplay = (po.payment_terms || '').trim();
  const advancePct = num(po.advance_payment_percentage);
  const advanceAmt = num(po.advance_payment_amount);

  const leftRows: Array<{ label: string; value: string }> = [
    { label: 'M/S', value: po.supplier_name || '' },
  ];
  if (supplierAddress) leftRows.push({ label: '', value: supplierAddress });
  if (supplierTrn) leftRows.push({ label: 'TRN', value: supplierTrn });
  leftRows.push({ label: 'Attn', value: po.supplier_contact || '—' });
  if (po.supplier_reference) leftRows.push({ label: 'Your ref.', value: po.supplier_reference });
  const rightRows: Array<{ label: string; value: string }> = [
    { label: 'TRN', value: trn },
    { label: 'PO No.', value: po.po_number || '' },
    { label: 'Date', value: poDate },
  ];
  if (po.po_type && po.po_type !== 'Standard') {
    rightRows.push({ label: 'PO type', value: po.po_type });
  }
  rightRows.push({ label: 'Currency', value: currency });
  if (deliveryDate) rightRows.push({ label: 'Delivery', value: deliveryDate });
  for (const row of refRows) rightRows.push(row);

  const columns: PrintColumn[] = [
    { key: 'sno', label: 'S.No', width: '36px' },
    { key: 'code', label: 'Code', width: '72px' },
    { key: 'description', label: 'Description' },
  ];
  if (showSpecCol) columns.push({ key: 'spec', label: 'Spec' });
  if (showDelCol) columns.push({ key: 'del', label: 'Del. date', width: '72px' });
  if (showProjectCol) columns.push({ key: 'project', label: 'Project' });
  columns.push(
    { key: 'qty', label: 'Qty', align: 'right', width: '52px' },
    { key: 'uom', label: 'UOM', width: '40px' },
    { key: 'unit', label: 'Unit price', align: 'right', width: '72px' }
  );
  if (showDiscCols) {
    columns.push(
      { key: 'discPct', label: 'Disc %', align: 'right', width: '48px' },
      { key: 'discAmt', label: 'Disc amt', align: 'right', width: '64px' }
    );
  }
  if (showVatCols) {
    columns.push(
      { key: 'vatPct', label: 'VAT %', align: 'right', width: '56px' },
      { key: 'vatAmt', label: 'VAT', align: 'right', width: '56px' }
    );
  }
  columns.push({ key: 'net', label: `Net (${currency})`, align: 'right', width: '72px' });

  const lines = sorted.map((line, idx) => {
    const extraLines: string[] = [];
    if (line.brand_offered) extraLines.push(`Brand: ${line.brand_offered}`);
    if (line.is_alternate_item) extraLines.push('Alternate item');
    if (line.lead_time_days != null && line.lead_time_days > 0) {
      extraLines.push(`Lead time: ${line.lead_time_days} day(s)`);
    }
    if (line.notes) extraLines.push(line.notes);
    if (line.stock_quantity != null && Number(line.stock_quantity) > 0) {
      const size = String((line as { stock_dimensions?: string | null }).stock_dimensions ?? '').trim();
      const total = `${Number(line.stock_quantity).toLocaleString()} ${line.stock_uom || ''}`.trim();
      extraLines.push(`Stock: ${[size, total].filter(Boolean).join(' · ')}`);
    }

    const cells: Record<string, string> = {
      code: line.item_code || '—',
      qty: formatQuantity(line.ordered_quantity, { maximumFractionDigits: 3 }),
      uom: line.uom || '—',
      unit: formatPoAmount(line.unit_price),
      net: formatPoAmount(line.net_amount ?? line.line_total),
    };
    if (showSpecCol) cells.spec = line.specifications?.trim() || '—';
    if (showDelCol) {
      cells.del = line.expected_delivery_date
        ? formatPrintDate(line.expected_delivery_date)
        : '—';
    }
    if (showProjectCol) cells.project = projectLabel(line);
    if (showDiscCols) {
      cells.discPct =
        num(line.discount_percentage) > 0
          ? formatPercent(line.discount_percentage, { semantics: 'whole', maximumFractionDigits: 3 })
          : '—';
      cells.discAmt = formatPoAmount(line.discount_amount ?? 0);
    }
    if (showVatCols) {
      cells.vatPct = lineTaxTypeLabel(line.vat_percentage, line.vat_applicable);
      cells.vatAmt = formatPoAmount(line.vat_amount ?? 0);
    }

    return {
      lineNumber: line.line_number ?? idx + 1,
      description:
        formatItemDescriptionWithProfile(
          line.supplier_description || line.item_description || line.item_code || '—',
          {
            ...(line as any),
            item_description: (line as any).catalog_item_description ?? undefined,
          }
        ) || '—',
      cells,
      extraLines,
    };
  });

  const shipLines: string[] = [];
  if (po.delivery_address) shipLines.push(po.delivery_address);
  if (po.site_location) shipLines.push(po.site_location);
  if (!po.delivery_to && !po.delivery_address && !po.site_location) {
    shipLines.push(companyInfo.address || '—');
  }

  const billLines: string[] = [];
  if (companyInfo.address) billLines.push(companyInfo.address);
  if (companyInfo.phone) billLines.push(`Tel: ${companyInfo.phone}`);
  if (companyInfo.email) billLines.push(`Email: ${companyInfo.email}`);
  if (trn) billLines.push(`TRN: ${trn}`);

  const termRows: Array<{ label: string; value: string }> = [
    { label: 'Payment', value: paymentDisplay || '—' },
    { label: 'Delivery', value: deliveryDisplay || '—' },
  ];
  if (advancePct > 0 || advanceAmt > 0) {
    termRows.push({
      label: 'Advance',
      value: [advancePct > 0 ? `${advancePct}%` : '', advanceAmt > 0 ? `${currency} ${formatPoAmount(advanceAmt)}` : '']
        .filter(Boolean)
        .join(' — '),
    });
  }
  if (po.notes) termRows.push({ label: 'Notes', value: po.notes });

  const termsExtra: string[] = [];
  if (po.terms_and_conditions) termsExtra.push(po.terms_and_conditions);
  if (po.special_instructions) {
    termsExtra.push(`Special instructions: ${po.special_instructions}`);
  }

  const subtitle =
    po.amendment_of_po_id && po.amendmentOf?.po_number
      ? `Amendment of PO ${po.amendmentOf.po_number}${revNo > 1 ? ` — Revision ${revNo}` : ''}`
      : revNo > 1
        ? `Revision ${revNo}`
        : undefined;

  const hasMilestones =
    po.payment_term_type === PO_PAYMENT_TERM_MILESTONE &&
    Array.isArray(po.milestones) &&
    po.milestones.length > 0;

  const overheadLines = Array.isArray(po.overheads) ? po.overheads : [];
  const hasOverheadLines = overheadLines.length > 0 && num(po.total_overhead) > 0;

  const milestoneNode = hasMilestones ? (
    <div className="print-letter-milestones">
      <h4>Milestone Payment Schedule</h4>
      <table className="print-generic-table">
        <thead>
          <tr>
            <th className="sno">#</th>
            <th>Milestone</th>
            <th>Type</th>
            <th>Fulfillment</th>
            <th className="num">%</th>
            <th className="num">Amount</th>
            <th>Expected</th>
          </tr>
        </thead>
        <tbody>
          {[...(po.milestones || [])]
            .sort((a, b) => (a.milestone_number ?? 0) - (b.milestone_number ?? 0))
            .map((m) => (
              <tr key={m.id}>
                <td className="sno">{m.milestone_number}</td>
                <td>{m.milestone_name}</td>
                <td>{m.milestone_type}</td>
                <td>
                  {PO_MILESTONE_FULFILLMENT_LABELS[m.fulfillment_method] ?? m.fulfillment_method}
                </td>
                <td className="num">{Number(m.percentage ?? 0).toFixed(2)}</td>
                <td className="num">{formatPoAmount(m.milestone_amount)}</td>
                <td>{m.expected_date ? formatPrintDate(m.expected_date) : '—'}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  ) : null;

  const overheadNode = hasOverheadLines ? (
    <div className="print-letter-milestones">
      <h4>Overheads</h4>
      <table className="print-generic-table">
        <thead>
          <tr>
            <th className="sno">#</th>
            <th>Type</th>
            <th>Description</th>
            <th className="num">Amount ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {[...overheadLines]
            .sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0))
            .map((oh, idx) => (
              <tr key={oh.id || `oh-${idx}`}>
                <td className="sno">{oh.line_number ?? idx + 1}</td>
                <td>{oh.overhead_type || '—'}</td>
                <td>{oh.description || oh.remarks || '—'}</td>
                <td className="num">
                  {/* Prefer document-currency amount; never base_currency_amount (company base / AED). */}
                  {formatPoAmount(oh.amount ?? oh.original_amount)}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  ) : null;

  const totalsBlock = (
    <div>
      <div className="print-letter-totals">
        <table>
          <tbody>
            <tr>
              <td>Subtotal :</td>
              <td>{formatPoAmount(subtotal)}</td>
            </tr>
            {commercial.discount > 0 ? (
              <tr>
                <td>{commercial.discountLabel} :</td>
                <td>{formatPoAmount(commercial.discount)}</td>
              </tr>
            ) : null}
            {commercial.discount > 0 ? (
              <tr>
                <td>Total after discount :</td>
                <td>{formatPoAmount(totalAfterDiscount)}</td>
              </tr>
            ) : null}
            <tr>
              <td>{vatSummary.vatLabel}</td>
              <td>{vatSummary.exempt ? 'Exempt' : formatPoAmount(vatSummary.vatAmount)}</td>
            </tr>
            {num(po.freight_charges) > 0 ? (
              <tr>
                <td>Freight :</td>
                <td>{formatPoAmount(po.freight_charges)}</td>
              </tr>
            ) : null}
            {num(po.other_charges) > 0 ? (
              <tr>
                <td>Other charges :</td>
                <td>{formatPoAmount(po.other_charges)}</td>
              </tr>
            ) : null}
            {num(po.total_overhead) > 0 ? (
              <tr>
                <td>Overhead :</td>
                <td>{formatPoAmount(po.total_overhead)}</td>
              </tr>
            ) : null}
            <tr className="grand-total">
              <td>Grand Total :</td>
              <td>{formatPoAmount(grand)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const amountWordsBlock = (
    <p className="print-letter-amount-words">
      <span>Total Only:</span>
      <strong>{formatPoAmountInWords(grand, currency)}</strong>
    </p>
  );

  const finals: Array<{ id: string; heightPx: number; keepTogetherWithNext?: boolean }> = [
    { id: 'totals', heightPx: estimatePoTotalsBlockHeight(po, commercial.discount), keepTogetherWithNext: true },
    { id: 'amountWords', heightPx: 32 },
  ];
  if (hasOverheadLines) {
    finals.push({
      id: 'overheads',
      heightPx: 48 + overheadLines.length * LINE_HEIGHT_PX + 24,
    });
  }
  if (hasMilestones) {
    finals.push({
      id: 'milestones',
      heightPx: 48 + (po.milestones?.length || 0) * LINE_HEIGHT_PX + 24,
    });
  }

  const isApproved = String(po.approval_status || '').trim() === 'Approved';

  return buildSimplePrintDocument({
    docType: 'purchase_order',
    title: 'PURCHASE ORDER',
    subtitle,
    documentNumber: po.po_number || 'PO',
    partyName: po.supplier_name || '',
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    // Unapproved prints must not look official — diagonal DRAFT on every page.
    watermark: isApproved ? undefined : { text: 'DRAFT' },
    continuationLabel: `${po.po_number || 'PO'} | ${po.supplier_name || ''}`,
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [
      'Please find below our official purchase order. Kindly acknowledge receipt and confirm delivery schedule, pricing, and terms as stated. We look forward to your confirmation at the earliest.',
    ],
    addressGrid: [
      { heading: 'Bill to', name: companyInfo.name, lines: billLines },
      {
        heading: 'Ship to',
        name: po.delivery_to || undefined,
        lines: shipLines,
      },
    ],
    columns,
    lines,
    finals,
    termsHeading: 'Terms & Conditions',
    termRows,
    termsExtra,
    signers: approvalSigners,
    renderPayload: {
      finalBlocks: {
        totals: totalsBlock,
        amountWords: amountWordsBlock,
        overheads: overheadNode,
        milestones: milestoneNode,
      },
    },
  });
}

export function buildSarPrintDocument(
  data: {
    advance_request_number: string;
    requested_date?: string;
    currency?: string;
    requested_amount?: number;
    purpose?: string | null;
    supplier_name?: string;
    po_number?: string;
    payment_terms?: string | null;
    notes?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
  },
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  approvalSigners: ApprovalPrintSigner[] = []
): PrintDocumentModel {
  const currency = data.currency || 'AED';
  const trn = formatTrnDisplay(companyInfo.taxNumber);

  const leftRows = [
    { label: 'M/S', value: data.supplier_name || '' },
    { label: 'Subject', value: data.purpose || 'Advance payment request' },
  ];
  const rightRows = [
    { label: 'TRN', value: trn },
    { label: 'SAR No.', value: data.advance_request_number },
    { label: 'Date', value: formatPrintDate(data.requested_date) },
    { label: 'PO No.', value: data.po_number || '' },
    { label: 'Currency', value: currency },
  ].filter((r) => r.value);

  const termRows: Array<{ label: string; value: string }> = [];
  if (data.payment_terms) termRows.push({ label: 'Payment', value: data.payment_terms });
  if (data.bank_name) termRows.push({ label: 'Bank', value: data.bank_name });
  if (data.bank_account) termRows.push({ label: 'Account', value: data.bank_account });
  if (data.notes) termRows.push({ label: 'Notes', value: data.notes });

  return buildSimplePrintDocument({
    docType: 'supplier_advance_request',
    title: 'SUPPLIER ADVANCE REQUEST',
    documentNumber: data.advance_request_number,
    partyName: data.supplier_name || '',
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    partyBlock: { leftRows, rightRows },
    introGreeting: 'Dear Sir / Madam,',
    introParagraphs: [
      'Please find below our supplier advance request. Kindly process as per the details stated.',
    ],
    columns: [
      { key: 'sno', label: '#' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', align: 'right' },
    ],
    lines: [
      {
        lineNumber: 1,
        description: data.purpose || 'Advance payment request',
        cells: { amount: formatPoAmount(data.requested_amount) },
      },
    ],
    finals: [
      { id: 'totals', heightPx: 48, keepTogetherWithNext: true },
      { id: 'amountWords', heightPx: 36 },
    ],
    termsHeading: termRows.length ? 'Terms & Notes' : undefined,
    termRows: termRows.length ? termRows : undefined,
    signers: approvalSigners,
    renderPayload: {
      finalBlocks: {
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr className="grand-total">
                  <td>Requested amount :</td>
                  <td>
                    {currency} {formatPoAmount(data.requested_amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
        amountWords: (
          <p className="print-letter-amount-words">
            <span>Total Only:</span>
            <strong>{formatPoAmountInWords(num(data.requested_amount), currency)}</strong>
          </p>
        ),
      },
    },
  });
}
